import { bool } from 'aws-sdk/clients/signer'
import { IMap } from './common'
import { IDriver, ISimpleDriverDTO } from './driver'
import { IAutoComplete, ILocation, ILocationObj, ILocationV2, ILocationStopV2 } from './location'
import { ISimpleProviderProduct, IAddOn } from './provider-product'
import { IRider } from './rider'
import { ISimpleService } from './service'
import { ILastActiveTrips } from './state'
import { IVehicle } from './vehicle'
import { ITripGroupRides } from './trip-group-rides'

export interface ITripImportResults {
  succeeded?: number
  failed?: number
  errors?: Array<{ row: number; error: string }>
}
export interface IManualCompleteTripState {
  completionType: string
  price: number
  isNotificationOn: boolean
  paymentRequired?: boolean
}
export interface ITripFilters {
  orgId?: string
  operatorId?: string[]
  riderId?: string[]
  riderIdWithName?: Array<{ value: string; label: string }>
  zoneId?: string
  serviceId?: string
  services?: string[]
  status?: string[]
  excludeStatuses?: string[]
  statusGroup?: string
  sortBy?: string[]
  productIds?: string[]
  serviceIds?: string[]
  timeStart?: string
  timeEnd?: string
  timeField?: string
  skip?: number
  take?: number
  timeFilter?: string
  feedbackType?: string
  feedbackStatus?: string
  userType?: string
  search?: string
  getDashBoardData?: boolean // flag for displaying dashboard data
  chargeCollection?: string
  timelineViewDisplay?: TimelineViewDisplayType
  startDate?: string
  endDate?: string
}
export enum TimelineViewDisplayType {
  DRIVER = 'Driver',
  VEHICLE = 'Vehicle'
}

export interface IWaypointFilters {
  orgId?: string
  tripId?: string
  operatorId?: string
  riderId?: string
  serviceId?: string
  sortBy?: string[]
  productIds?: string[]
  timeStart?: string
  timeEnd?: string
  timeField?: string
  skip?: number
  take?: number
}

export interface IActiveTripResponse {
  id: any
  rider: IRider
  driver: IDriver
  location?: ILocationObj
  timeToPickUp: any | null
  timeToDropOff: any | null
  createAt: any
  scheduled: any | null
  statuses: ITripStatusesObj[]
  tripsGroupRides?: ITripGroupRides[]
}

export interface IRecentTrips {
  today: IRecentTripObj[]
  recent: IRecentTripObj[]
}
export interface IRecentTripObj {
  id: string
  distance: number
  duration: number
  status: ITripStatusesObj[]
  lastStatus: string
  lastStatusId: number
  estimatedDuration: number
  lastStatusAt: string
  product: ISimpleProviderProduct
  pickupLocation: ILocationV2
  dropoffLocation: ILocationV2
  rider: ITripRider
  polyline: Array<{ lat: number; lng: number; bearing: number; updatedAt: string }>
  service: {
    name: string
    serviceId: string
  }
  operator?: ISimpleDriverDTO
  vehicle?: {
    vehicleId: string
    make: string
    model: string
    licensePlate: string
  }
  notes?: string
  scheduledPickupTime?: string
  scheduledDropoffTime?: string
  tripType?: string
}

export interface ITripReceipt {
  amountCharged: number
  servicePrice: number
  providerPrice: number
  distance: number
  duration: number
}

export interface IMapBound {
  north: number
  south: number
  east: number
  west: number
}

export interface IDestinations extends ILocationV2 {
  location: ILocationV2
  sequenceNo: number
}

export interface IActiveTripResponseV2 {
  id: string
  distance: number
  duration: number
  estimatedPickup: number
  estimatedPrice: number
  estimatedFare: number
  estimatedDistance: number
  estimatedDuration: number
  groupRideId?: string
  operatorLocation: {
    coordinates: number[]
  }
  pickupLocation: ILocationV2
  dropoffLocation: ILocationV2
  destinations?: IDestinations[]
  rider: ITripRider
  status: ITripStatusesObj[]
  lastStatus: string
  lastStatusId: number
  lastStatusAt: string
  product: ISimpleProviderProduct
  polyline: Array<{ lat: number; lng: number; bearing: number; updatedAt: string }>
  routePolyline: string
  bounds: IMapBound
  service: {
    name: string
    serviceId: string
  }
  startMileage: string
  pickUpFlag: number
  endMileage: string
  dropOffFlag: number
  operator?: ISimpleDriverDTO
  vehicle?: {
    vehicleId: string
    make: string
    model: string
    licensePlate: string
  }
  scheduledPickupTime?: string
  scheduledDropoffTime?: string
  leaveAtDateTime?: string
  arriveByDateTime?: string
  scheduleType: 'on_demand' | 'arrive_by' | 'leave_by'
  notes?: string
  confirmationNumber?: string
  providerOneNumber?: string
  organizationId?: string
  createdAt?: string
  tripEditsHistory?: Array<{ id: string; createdAt: string; updatedBy: string; prevData: string; currentData: string }>
  riderCount?: number
  details?: any
  addOn?: { [key: string]: number }
  representativeName?: string
  representativeSignature?: string
  representativeTitle?: string
  driverSignature?: string
  paymentMode?: string
  riderSignature?: string
  tripsGroupRides: ITripGroupRides[]
  tripRequester?: string
  averageEta?: number
  rideReconfirmationCount?: number
  tripCode?: string
  tripType?: string
  subStatus: ISubstatus[]
  lastSubStatus: string
  lastSubStatusAt: string
  isUpdatedRecurringTrip?: boolean
}

export interface ISubstatus {
  id: string
  tripId: string
  createdBy: string
  createdAt: string
  subStatus: string
}
export interface ITripEstimatesObject {
  eta?: number
  price?: number
  fare?: number
  duration?: number
  distance?: number
}

export interface ITripRequestObject {
  id?: any
  organizationId: string
  product: {
    productId: string
    name: string
    integrationId?: string
    pricingId?: string
    bookingPriority?: string
  }
  estimates?: ITripEstimatesObject
  service: ISimpleService
  pickupLocation: ILocationV2
  dropoffLocation: ILocationV2
  additionalStop: ILocationStopV2[]
  rider?: ITripRider
  notes?: string
  routePolyline?: string
  bounds?: object
  scheduledPickupTime?: string
  leaveAtDateTime?: string
  arriveByDateTime?: string
  scheduledDropoffTime?: string
  recurringPickupTime?: string
  recurringDropoffTime?: string
  tripType: string
  timesToRecur?: number
  timesToRunLeft?: number
  crontabExp?: string
  runForever?: bool
  additionalRiders?: number
  currentServiceId?: string
  tripDoubleCheckerEnable?: boolean
  bookDoubleTrip?: boolean
  endDate?: string
  details: any
  willCall?: boolean
  paymentMode?: string
  confirmationNumber?: string
  providerOneNumber?: string
  weatherMain?: string
  passengerDetails: ITripPassengerDetail
  totalRidersFare: number
  tripRequester?: string
  bookingPriority?: string
  timezone: string
  distance?: number
  duration?: number
  scheduledReturn?: boolean
  returnPickupTime?: string
  isReturnedTrip?: boolean
}
export interface ITripPassengerDetail {
  tripRequestRiders: ITripRequestRiders[]
  disabilityApproved?: boolean
  attendant?: boolean
  amount?: number
  totalAmount?: number
}
export interface IPassenger {
  tripId?: string
  riderType: string
  fareType?: string
  totalRider: number
  amount?: number
}
export interface ITripRequestRiders {
  fareType: string | null
  riderType: string
  totalRider: number
  amount?: number
  totalAmount?: number
}
export interface ITripRequestRidersHelper {
  riders: ITripRequestRiders[]
  totalRiders: number
}
export interface ISelectedTriptRidersSelector {
  riders: ITripGroupRides[]
  totalRiders: number
}
export interface IAuditLog {
  log: string
  date: Date
}

export interface IFeedBack {
  rating: number
  message: string
  tags: string[]
}

export interface ITripDescription {
  address: string
  exactAddress: string
}

export interface ITripHistoryObject {
  id: number
  rider: IRider
  driver: IDriver
  vehicle: IVehicle
  location: ILocationObj
  riderCount: number
  status: string
  dropOffTime: Date
  pickUpTime: Date
  createdAt: Date
  cost: number
  fare: number
  service: any
  provider: string
  feedback: IFeedBack
  auditLogs: IAuditLog[]
  //timings
  wasRequestingTime: number
  wasWaitingTime: number
  travelTime: number
  travelDistance: number
  pickupDwell: number
  dropOffDwell: number
  //trip details
  wheelchair: number
  attendant: boolean
  leavingTime: Date
  assistance: string
}

export interface ITripObject {
  id: string
  mobilityOptionId: number
  assisstanceId: number
  programServiceId: number
  providerProductId: string
  operatorId: number
  vehicleId: number
  riderId: string
  riderCount: number
  status: string
  dropOffTime: Date
  pickUpTime: Date
  cost: number
  fare: number
  pickUpNote: string
  dropOffNote: string
  pickUpLocationId: number
  dropOffLocationId: number
  feedback: string
  tags: string[]
  wasRequesting: number // any time is in seconds
  wasWaitingForPickup: number
  travelTime: number
  travelDistance: number // in miles
  routePolyline?: string
  lastStatus: string
  lastStatusId: number
  lastStatusAt: string
  bounds?: any
}

export interface IDestinations extends ILocationV2 {
  location: ILocationV2
}

export interface IRiderTrip {
  id: string
  pickupLocation: ILocationV2
  dropoffLocation: ILocationV2
  destinations?: IDestinations[]
  product?: ISimpleProviderProduct
  rider?: ITripRider
  riderCost?: number
  notes?: string
  confirmationNumber?: string
  providerOneNumber?: string
  scheduledPickupTime?: string
  scheduledDropoffTime?: string
  createdAt?: string
  lastStatusAt?: string
  details?: any
  passengerDetails?: ITripPassengerDetail
  tripsGroupRides?: ITripGroupRides[]
  tripRequester?: string
  routePolyline?: string
}

export interface ITripRider {
  id?: string
  firstName: string
  lastName: string
  riderId: string
  photo?: string
  email?: string
  countryCode: string
  phoneNumber: string
  wallet?: IBasicRiderWalletBalance
  birthDate?: string
}
export interface IBasicRiderWalletBalance {
  balance: number
  bonusBalance: number
  userId: string
  orgId: string
}

export interface ITripStatus {
  id?: string
  comment?: string
  createdAt: Date
  createdBy?: string
  status: string
}

export interface ITripStatusesObj {
  id?: string
  status: string
  comment?: string
  createdAt: string
  createdBy?: string
  statusId?: number
  teamMember?: ITeamMember
  user?: IStatusUser
}
export interface ITeamMember {
  id: string
  userId: string
  firstName: string
  lastName: string
}
export interface IStatusUser {
  createdAt: string
  firstName: string
  id: string
  lastName: string
  updatedAt: string
  userId: string
}
export interface IUserTrips {
  driver: IDriver
  id: number
  location: ILocation
  rider: IRider
  status: string
}

export interface IActiveTripsByStagesResponse {
  stage: string
  items: IActiveTripResponseV2[]
  total: number
}

export interface IActiveTripsConvertedToObject {
  items: IMap<IActiveTripResponseV2>
  lastActiveTrips: IMap<ILastActiveTrips>
}

export interface ITripAvailableProduct {
  id: string
  name: string
  price: number
  serviceId: string
  serviceName: string
  serviceBeginsAt?: Date | undefined
  serviceExpiresAt?: Date | undefined | null
  serviceType?: string | undefined
  pricingId?: string
  baseFare?: number
  fare: number
  promoLimit: number
  promoFare: number
  tripsCount: number
  promoDuration: string
  eta: number
  integrationId?: string
  externalUrl?: string
  availableDrivers?: number
  busyDrivers?: number
  serviceMembershipRequired: boolean
  riderHasMembership: boolean
  unavailable: boolean
  unavailableReason: string
  promoTripCount: number
  completedTripCount: number
  serviceTripLimit: number
  serviceLimitDuration: string
  viewOption?: string
  addOn?: IAddOn[]
  bookingPriority?: string
}

export interface ITripExtraParams {
  addOnCost?: number
  addOnFare?: number
  addOn?: { [key: string]: number }
  willCall?: boolean
  scheduledReturn?: boolean
  returnPickupTime?: string
}

export interface IIntegrationProductResponse extends ITripAvailableProduct {
  productName: string
  productId: string
}

export interface ITripProductPricing {
  productId: string
  cost: number
  fare: number
}

export interface ITripRequestSettings {
  dropoffAutoComplete: IAutoComplete[]
  pickupAutoComplete: IAutoComplete[]
  additionalStopAutoComplete: IAutoComplete[]
  riderId: string
  availableProducts: ITripAvailableProduct[]
  productPricing: IMap<ITripProductPricing>
  rider: string
  userTrips: IUserTrips[]
  activeTrips: IActiveTripResponse[]
  tripsSettings: {}
  currentTrip: IActiveTripResponse | null
  query: string
  currentTripFail: string | null
  currentTripRequest: boolean
}

export interface ILastActiveTripsByStages {
  WAITING_FOR_PICKUP: ILastActiveTrips
  REQUESTING: ILastActiveTrips
  EN_ROUTE: ILastActiveTrips
}

export interface ITripStage {
  label: string
  route: string
  value: string
}

export interface ITripSettings {
  showMembers: boolean
}

export interface IDropOffNote {
  prevEndMileage?: string
  endMileage?: string
  dropOffFlag?: number
}

export interface IPickupNote {
  prevStartMileage?: string
  startMileage?: string
  pickUpFlag?: number
}

export interface ITripEdit extends IPickupNote, IDropOffNote {
  tripAction?: 'EDIT_TRIP_DETAILS' | 'MANUAL_CANCEL_TRIP' | 'MANUAL_COMPLETE_TRIP' // Specify trip actions to be use as reference when to send a push notification for trip notes
  scheduledPickupTime?: string
  scheduledDropoffTime?: string
  notes?: string
  prevScheduledPickupTime?: string | undefined
  prevScheduledDropoffTime?: string | undefined
  prevNotes?: string | undefined
  prevOperator?: string | undefined
  operator?: string | undefined
  feedbackStatus?: string | undefined
  prevFeedbackStatus?: string | undefined
  tripId?: string | undefined
  leaveAtDateTime?: string
  arriveByDateTime?: string
  prevLeaveAtDateTime?: string | undefined
  prevArriveByDateTime?: string | undefined
}

export interface IDailyTripLogReq {
  serviceId: string
  fromDate: string
  toDate: string
  productIds: string[]
  statusGroup?: string
}

export interface ITripLogReq {
  serviceId: string
  fromDate: string
  toDate: string
  productIds: string[]
}

export interface ICountTripStatusFilter {
  orgId: string
  timeStart: string
  timeEnd: string
  serviceId?: string
}
export interface ICountTripStatusResp {
  last_status: string
  count: number
}

export interface IServiceListFilterDTO {
  fromDate: string
  toDate: string
  lastStatusIds: string
}

export interface ITripLastStatus {
  id: string
  last_status_id: number
  last_status: string
  last_status_at: string
  created_at: Date
}
export interface ITotalTripsPerServiceResponseDTO {
  service_name: string
  service_id: string
  total_trips: string
  total_booked_trips?: string
  trip_statuses: ITripLastStatus[]
}

//Weather
export interface WeatherDTO {
  id: string
  address: string
  main: string
  icon: string
  temp: number
  createdAt?: string
  updatedAt?: string
}

export interface WeatherQueryParams {
  date?: string
  address: string
}

export interface SelectedAddOn {
  [key: string]: number
}

export interface ITempVehicleCount {
  temporary_vehicle: string
  total: string
}

export interface TemporaryVehicleDTO {
  id: string
  name: string
  createdAt?: string
  updatedAt?: string
}

export interface TempVehicleCountByServiceDTO {
  serviceid: string
  name: string
  total: number
}

export interface TemporaryVehiclesResponseDTO {
  success: boolean
  message: string
  data: TemporaryVehicleDTO[]
}

export interface TempVehicleCountByServiceResponseDTO {
  success: boolean
  data: TempVehicleCountByServiceDTO[]
}

export interface GenerateRecurringTripsRequestDTO {
  serviceId: string
  startDate: string
  endDate: string
  timezoneId: string
}

export interface ITempVehicleTripsCountResponseDTO {
  success: boolean
  serviceId: string
  serviceName: string
  totalTrips: ITempVehicleCount[]
}

export interface IGetScheduledRequestedTripsResponseDTO {
  totalTrips: number
}

export interface IPushNotificationBody {
  title: string
  body: string
}

export interface IPushNotificationData {
  extraInfo?: {} | null
  tripId: string
}

export interface IPushNotificationPayload {
  notification: IPushNotificationBody
  data: IPushNotificationData
}



// WEBPACK FOOTER //
// ./src/types/trip.ts